import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import "../asstes/styles/quizPage.scss";

import Stepper from "./ReusableComponent/Quiz/Stepper";
import StepRenderer from "./ReusableComponent/Quiz/StepRenderer";
import QuizLayout from "./ReusableComponent/Quiz/QuizLayout";
import SuccessModal from "./ReusableComponent/Quiz/SuccessModal";

import Speaker from "../asstes/images/podcast/podcast-detail-speaker.png";
import AboutCorporateTrainings from "../asstes/images/training/about-training-corporate.png";
import TrainerProfileView from "../asstes/images/training/trainer-profile.png";
import philosopher from "../asstes/images/about/philosopher.png";

const stepsData = [
    {
        id: "focus",
        type: "options",
        title: "What areas of your life do you want to focus on?",
        options: [
            "Mindset",
            "Wealth",
            "Health",
            "Relationships",
            "Business",
            "Leadership",
            "Happiness"
        ],
        image: Speaker
    },
    {
        id: "contactInfo",
        type: "multiInput",
        title: "Please provide your contact information",
        fields: [
            {
                id: "name",
                placeholder: "Enter your name",
                type: "text"
            },
            {
                id: "email",
                placeholder: "Enter your email",
                type: "email"
            },
            {
                id: "phone",
                placeholder: "Enter your phone number",
                type: "tel"
            }
        ],
        image: AboutCorporateTrainings
    },
    {
        id: "goal",
        type: "textarea",
        title: "Describe your goal",
        placeholder: "Write your goal...",
        image: TrainerProfileView
    },
    {
        id: "commitment",
        type: "options",
        title: "How committed are you?",
        options: ["Low", "Medium", "High"],
        image: philosopher
    }
];

const RequestPrivateAlignmentReview = () => {

    const [step, setStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const navigate = useNavigate();

    const current = stepsData[step];

    const handleChange = (value) => {
        setFormData({
            ...formData,
            [current.id]: value
        });

        setError("");
    };

    const submitSessionDetails = async () => {
        setIsSubmitting(true);
        setError("");

        const payload = {
            name: formData.contactInfo?.name?.trim() || "",
            email: formData.contactInfo?.email?.trim() || "",
            phone: formData.contactInfo?.phone?.trim() || "",
            focus_area: formData.focus || "",
            goal_des: formData.goal?.trim() || "",
            commited_type: formData.commitment || ""
        };

        try {
            const baseUrl =
                process.env.NEXT_PUBLIC_API_BASE_URL ||
                process.env.REACT_APP_API_BASE_URL ||
                "";
            const apiUrl = baseUrl
                ? `${baseUrl.replace(/\/+$/, "")}/api/session-details/store`
                : "/api/session-details/store";

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (response.ok && !result.has_error) {
                setSuccessMessage(result.message || "Session request submitted successfully.");
                setShowModal(true);
            } else {
                setError(result.message || "Failed to submit request. Please try again.");
            }
        } catch (err) {
            console.error("Error submitting session details:", err);
            setError("Something went wrong. Please check your connection and try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const next = () => {
        if (isSubmitting) return;

        // Validation for multiInput type
        if (current.type === "multiInput") {
            const missingFields = current.fields.filter(field => 
                !formData[current.id]?.[field.id]?.trim?.()
            );
            
            if (missingFields.length > 0) {
                setError("⚠️ Please fill in all fields before continuing");
                return;
            }

            const email = formData[current.id]?.email?.trim?.();
            if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                setError("⚠️ Please enter a valid email address");
                return;
            }
        } else if (!formData[current.id]) {
            setError("⚠️ Please complete this step before continuing");
            return;
        }

        setError("");

        if (step < stepsData.length - 1) {
            setStep(step + 1);
        } else {
            submitSessionDetails();
        }
    };

    const back = () => {
        if (isSubmitting) return;
        if (step > 0) {
            setStep(step - 1);
            setError("");
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({});
        setStep(0);
        navigate("/PrivateCouncilNewPage");
    };

    return (
        <>
            <QuizLayout
                image={current.image}
                leftContent={
                    <AnimatePresence mode="wait">

                        <motion.div
                            key={step}
                            initial={{
                                opacity: 0,
                                y: 60,
                                scale: 0.95
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                                scale: 1
                            }}
                            exit={{
                                opacity: 0,
                                y: -60,
                                scale: 0.95
                            }}
                            transition={{
                                duration: 0.6,
                                ease: "easeInOut"
                            }}
                            className="quiz-motion-wrapper"
                        >

                            {/* STEP TEXT */}
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="quiz-step-count"
                            >
                                STEP {step + 1} OF {stepsData.length}
                            </motion.p>

                            {/* STEPPER */}
                            <motion.div
                                initial={{ opacity: 0, width: 0 }}
                                animate={{ opacity: 1, width: "100%" }}
                                transition={{ delay: 0.3 }}
                            >
                                <Stepper
                                    current={step + 1}
                                    total={stepsData.length}
                                />
                            </motion.div>

                            {/* TITLE */}
                            <motion.h2
                                initial={{
                                    opacity: 0,
                                    y: 40
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0
                                }}
                                transition={{
                                    delay: 0.4,
                                    duration: 0.6
                                }}
                                className="quiz-title"
                            >
                                {current.title}
                            </motion.h2>

                            {/* FORM */}
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: 30
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0
                                }}
                                transition={{
                                    delay: 0.5,
                                    duration: 0.6
                                }}
                            >
                                <StepRenderer
                                    step={current}
                                    value={formData[current.id]}
                                    onChange={handleChange}
                                />
                            </motion.div>

                            {/* ERROR */}
                            <AnimatePresence>
                                {error && (
                                    <motion.p
                                        initial={{
                                            opacity: 0,
                                            y: 10
                                        }}
                                        animate={{
                                            opacity: 1,
                                            y: 0
                                        }}
                                        exit={{
                                            opacity: 0,
                                            y: -10
                                        }}
                                        className="quiz-error mt-2"
                                    >
                                        {error}
                                    </motion.p>
                                )}
                            </AnimatePresence>

                            {/* BUTTONS */}
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: 30
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0
                                }}
                                transition={{
                                    delay: 0.7
                                }}
                                className="btn-toolbar-title quiz-actions"
                            >

                                {step > 0 && (
                                    <motion.button
                                        whileHover={!isSubmitting ? {
                                            scale: 1.05
                                        } : {}}
                                        whileTap={!isSubmitting ? {
                                            scale: 0.95
                                        } : {}}
                                        className="btn bh-btn-dark view-all"
                                        onClick={back}
                                        disabled={isSubmitting}
                                    >
                                        Back
                                    </motion.button>
                                )}

                                <motion.button
                                    whileHover={!isSubmitting ? {
                                        scale: 1.05
                                    } : {}}
                                    whileTap={!isSubmitting ? {
                                        scale: 0.95
                                    } : {}}
                                    className="btn bh-btn-dark view-all"
                                    onClick={next}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting
                                        ? "Submitting..."
                                        : step === stepsData.length - 1
                                            ? "Submit"
                                            : "Next Step"}
                                </motion.button>

                            </motion.div>

                        </motion.div>

                    </AnimatePresence>
                }
            />

            <SuccessModal
                show={showModal}
                onClose={handleCloseModal}
                message={successMessage}
            />
        </>
    );
};

export default RequestPrivateAlignmentReview;
